import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Button, Dialog, Combobox, Badge } from "@/components/ui";
import { DataTable } from "@/components/ui/data-table";
import { ColumnDef } from "@tanstack/react-table";
import {
  Form,
  FormResponse,
  PlayersServices,
  FanServices,
  FormServices,
  FIELD_TYPE_MAP,
  FormFieldType,
} from "@/services/bedrock";
import { useQuery } from "@tanstack/react-query";
import { usePaginatedCombobox, useSocialCoachSession } from "@/utils";
import { EmailLink, PhoneLink } from "@/components/ui";
import { ClipboardList, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { exportToXlsx, getAutoWidths } from "@/utils/xlsxExportHelpers";

const STATUS_OPTIONS = [
  { id: "initialized", label: "Initialized" },
  { id: "in_progress", label: "In Progress" },
  { id: "completed", label: "Completed" },
];

const getPrioritizedResponse = (fieldResponses: any[]) => {
  const answered = fieldResponses.filter(f => f.value !== null && f.value !== undefined);
  if (answered.length === 0) return null;

  // Prioritize star -> nps -> ten_point -> sentiment -> first answered field
  const starField = answered.find(f => f.formField?.fieldType === "star");
  if (starField) return starField;

  const npsField = answered.find(f => f.formField?.fieldType === "nps");
  if (npsField) return npsField;

  const tenPointField = answered.find(f => f.formField?.fieldType === "ten_point");
  if (tenPointField) return tenPointField;

  const sentimentField = answered.find(f => f.formField?.fieldType === "sentiment");
  if (sentimentField) return sentimentField;

  return answered[0] || null;
};

const transformResponsesForExport = (responses: FormResponse[], formFields: any[]) => {
  return responses.map(resp => {
    // Basic Metadata
    const row: Record<string, any> = {
      "Response ID": resp.id,
      "Date Submitted": resp.completedAt
        ? new Date(resp.completedAt).toLocaleString()
        : resp.createdAt
        ? new Date(resp.createdAt).toLocaleString()
        : "-",
      Status: resp.status === "completed" ? "Completed" : resp.status === "in_progress" ? "In Progress" : "Initialized",
      "Test Run": resp.isTest ? "Yes" : "No",
      "Respondent Name": resp.fan?.displayName || "Anonymous",
      "Respondent Email": resp.fan?.email || "-",
      "Respondent Phone": resp.fan?.phoneNumber || "-",
      "Assigned Player": resp.player?.fullName || "-",
      "Player Email": resp.player?.email || "-",
    };

    // Dynamic Columns for each Question Field
    formFields.forEach(field => {
      const fieldResp = (resp.formFieldResponses || []).find(
        r => r.formField?.id === field.id || r.formField?.fieldKey === field.fieldKey
      );
      let answerText = "-";
      if (fieldResp && fieldResp.value !== null && fieldResp.value !== undefined && fieldResp.value !== "") {
        const rawVal = fieldResp.value;
        const fieldType = fieldResp.formField?.fieldType || field.fieldType;

        if (fieldType === "star") {
          let starVal: { rating?: number; text?: string } | null = null;
          if (typeof rawVal === "object") {
            starVal = rawVal;
          } else if (typeof rawVal === "string") {
            try {
              starVal = JSON.parse(rawVal);
            } catch {}
          }
          if (starVal) {
            answerText = starVal.rating
              ? `${starVal.rating} Stars${starVal.text ? ` - "${starVal.text}"` : ""}`
              : starVal.text || "-";
          } else {
            answerText = `${rawVal} Stars`;
          }
        } else if (fieldType === "boolean") {
          const isYes =
            rawVal === true || String(rawVal).toLowerCase() === "true" || String(rawVal).toLowerCase() === "yes";
          answerText = isYes ? "Yes" : "No";
        } else if (fieldType === "sentiment") {
          const sentimentMap: Record<string, string> = {
            positive: "Positive",
            neutral: "Neutral",
            negative: "Negative",
          };
          answerText = sentimentMap[String(rawVal).toLowerCase().trim()] || String(rawVal);
        } else {
          answerText = String(rawVal);
        }
      }

      const columnHeader = field.title || field.fieldKey || "Question";
      row[columnHeader] = answerText;
    });

    return row;
  });
};

interface FormResponsesListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: Form | null;
  formRequestId?: number | null;
}

export function FormResponsesListDialog({ open, onOpenChange, form, formRequestId }: FormResponsesListDialogProps) {
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 });
  const [viewingResponse, setViewingResponse] = useState<FormResponse | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<typeof STATUS_OPTIONS>([
    { id: "in_progress", label: "In Progress" },
    { id: "completed", label: "Completed" },
  ]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const handleExportToXlsx = async () => {
    if (!form?.id) return;
    setIsExporting(true);
    setExportProgress(0);
    try {
      let responseList: FormResponse[] = [];
      let currentPage = 1;
      let totalPages = 1;
      const perPage = 250;

      do {
        const result = await FormServices.getFormResponses({
          formId: form.id,
          playerId: players.value?.id,
          fanId: fans.value?.id,
          formRequestId: formRequestId || undefined,
          status: selectedStatuses.map(s => s.id) as any,
          page: currentPage,
          perPage,
          extraFields: ["player"],
        } as any);

        const content = result.content || [];
        responseList = responseList.concat(content);
        totalPages = result.pagination?.totalPages || 1;

        const progressPercent = Math.round((currentPage / totalPages) * 100);
        setExportProgress(progressPercent);

        currentPage++;
      } while (currentPage <= totalPages);

      if (!responseList.length) return;

      const exportData = transformResponsesForExport(responseList, form.formFields || []);

      const sheets = [
        {
          name: `${form.displayName || "Responses"}`.substring(0, 31),
          data: exportData,
          columns: getAutoWidths(exportData),
          headerRows: [
            [`SocialCoach Form Responses Report`],
            [`Form: ${form.displayName || "Unknown Form"}`],
            [`Exported At: ${new Date().toLocaleString()}`],
            [`Total Records: ${responseList.length}`],
            [],
          ],
        },
      ];

      const filename = `Form_Responses_${(form.displayName || "Report").replace(/\s+/g, "_")}_${new Date()
        .toISOString()
        .slice(0, 10)}`;
      await exportToXlsx(filename, sheets);
    } catch (err) {
      console.error("Failed to export responses to XLSX:", err);
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  const renderFieldValue = (fieldResp: any) => {
    const fieldType = fieldResp.formField?.fieldType;
    const rawVal = fieldResp.value;

    if (rawVal === null || rawVal === undefined || rawVal === "") {
      return <span className="text-slate-300 italic font-normal">No Answer</span>;
    }

    if (fieldType === "star") {
      let starVal: { rating?: number; text?: string } | null = null;
      if (typeof rawVal === "object") {
        starVal = rawVal;
      } else if (typeof rawVal === "string") {
        try {
          starVal = JSON.parse(rawVal);
        } catch {
          // If not valid JSON, treat as raw number
        }
      }

      if (starVal) {
        const rating = Number(starVal.rating);
        return (
          <div className="space-y-1.5">
            {!isNaN(rating) && (
              <div className="flex items-center gap-0.5 text-amber-400 select-none">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={cn("size-4", i < rating ? "fill-amber-400 text-amber-400" : "text-slate-200")}
                  />
                ))}
              </div>
            )}
            {starVal.text && (
              <p className="text-slate-600 italic text-sm border-l-2 border-slate-200 pl-2.5 leading-normal">
                "{starVal.text}"
              </p>
            )}
          </div>
        );
      }

      const rating = Number(rawVal);
      if (!isNaN(rating)) {
        return (
          <div className="flex items-center gap-0.5 text-amber-400 select-none">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className={cn("size-4", i < rating ? "fill-amber-400 text-amber-400" : "text-slate-200")} />
            ))}
          </div>
        );
      }
    }

    if (fieldType === "nps" || fieldType === "ten_point") {
      const score = Number(rawVal);
      if (!isNaN(score)) {
        return (
          <span className="font-semibold text-slate-800 text-sm">
            {score} <span className="text-slate-400 font-normal text-xs">/ 10</span>
          </span>
        );
      }
    }

    if (fieldType === "boolean") {
      const isYes =
        rawVal === true || String(rawVal).toLowerCase() === "true" || String(rawVal).toLowerCase() === "yes";
      return (
        <span className={cn("font-semibold text-sm", isYes ? "text-emerald-600" : "text-amber-600")}>
          {isYes ? "Yes" : "No"}
        </span>
      );
    }

    if (fieldType === "sentiment") {
      const sentimentMap: Record<string, string> = {
        positive: "😊 Positive",
        neutral: "😐 Neutral",
        negative: "😞 Negative",
      };
      const cleaned = String(rawVal).toLowerCase().trim();
      const mapped = sentimentMap[cleaned];
      if (mapped) {
        return <span className="font-semibold text-slate-800 text-sm">{mapped}</span>;
      }
    }

    return <span className="text-slate-700 font-sans break-words">{String(rawVal)}</span>;
  };

  const renderInlineFieldValue = (fieldResp: any) => {
    const fieldType = fieldResp.formField?.fieldType;
    const rawVal = fieldResp.value;

    if (rawVal === null || rawVal === undefined || rawVal === "") {
      return <span className="text-slate-300 italic font-normal">No Answer</span>;
    }

    if (fieldType === "star") {
      let starVal: { rating?: number; text?: string } | null = null;
      if (typeof rawVal === "object") {
        starVal = rawVal;
      } else if (typeof rawVal === "string") {
        try {
          starVal = JSON.parse(rawVal);
        } catch {}
      }

      if (starVal) {
        const rating = Number(starVal.rating);
        return (
          <div className="flex flex-col gap-1 w-full min-w-0">
            {!isNaN(rating) && (
              <div className="flex items-center gap-0.5 text-amber-400 select-none shrink-0">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={cn("size-3.5", i < rating ? "fill-amber-400 text-amber-400" : "text-slate-200")}
                  />
                ))}
              </div>
            )}
            {starVal.text && (
              <span className="text-slate-500 italic text-xs truncate block w-full">"{starVal.text}"</span>
            )}
          </div>
        );
      }
    }

    if (fieldType === "text" || fieldType === "long_text") {
      return <span className="text-slate-700 font-sans truncate block w-full">{String(rawVal)}</span>;
    }

    return renderFieldValue(fieldResp);
  };

  const { isPlayer, playerId: loggedInPlayerId } = useSocialCoachSession();

  // Step 1: get players list for filter
  const getPlayers = async (search: string | undefined, page: number) => {
    const result = await PlayersServices.getPlayers({ search, page, perPage: 25 });
    return {
      ...result,
      content: result.content.map(player => ({
        id: player.id,
        label: `${player.fullName || "No Name"} - ${player.email}`,
      })),
    };
  };

  const players = usePaginatedCombobox(getPlayers, false);
  const activePlayerId = isPlayer ? loggedInPlayerId : players.value?.id;

  // Step 2: get fans list for filter (filtered by player if selected)
  const getFans = useCallback(
    async (search: string | undefined, page: number) => {
      const result = await FanServices.getFans({
        search,
        page,
        perPage: 25,
        playerId: activePlayerId,
      } as any);
      return {
        ...result,
        content: result.content.map(fan => ({
          id: fan.id,
          label: `${fan.displayName || "No Name"}${fan.email ? ` - ${fan.email}` : ""}`,
          original: fan,
        })),
      };
    },
    [activePlayerId]
  );

  const fans = usePaginatedCombobox(getFans, false);

  // Clear fan selection when player selection changes
  useEffect(() => {
    if (fans.value !== null && fans.value !== undefined) {
      fans.onValueChange(null as any, undefined);
    }
  }, [activePlayerId]);

  // Reset filter selections when opening dialog
  useEffect(() => {
    if (open) {
      setViewingResponse(null);
      setSelectedStatuses([
        { id: "in_progress", label: "In Progress" },
        { id: "completed", label: "Completed" },
      ]);
      setPagination(prev => {
        if (prev.page === 1 && prev.perPage === 10) return prev;
        return { page: 1, perPage: 10 };
      });
      if (players.value !== null && players.value !== undefined) {
        players.onValueChange(null as any, undefined);
      }
      if (fans.value !== null && fans.value !== undefined) {
        fans.onValueChange(null as any, undefined);
      }
    }
  }, [open]);

  // Fetch paginated responses list with selected filters
  const { data, isLoading } = useQuery({
    queryKey: [
      "formResponsesList",
      form?.id,
      pagination,
      activePlayerId,
      fans.value?.id,
      formRequestId,
      selectedStatuses,
    ],
    queryFn: () =>
      FormServices.getFormResponses({
        formId: form?.id,
        playerId: activePlayerId,
        fanId: fans.value?.id,
        formRequestId: formRequestId || undefined,
        status: selectedStatuses.map(s => s.id) as any,
        page: pagination.page,
        perPage: pagination.perPage,
        extraFields: ["player"],
      } as any),
    enabled: (!!form?.id || !!formRequestId) && open,
    placeholderData: prev => prev,
  });

  const getDurationString = (startedStr: string, completedStr: string | null) => {
    if (!completedStr) return null;
    try {
      const start = new Date(startedStr);
      const end = new Date(completedStr);
      const diffMs = end.getTime() - start.getTime();
      if (diffMs <= 0) return null;

      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);

      if (diffHours > 0) {
        const remainingMins = diffMins % 60;
        return `${diffHours}h ${remainingMins}m`;
      }
      if (diffMins > 0) {
        const remainingSecs = diffSecs % 60;
        return `${diffMins}m ${remainingSecs}s`;
      }
      return `${diffSecs}s`;
    } catch {
      return null;
    }
  };

  // Table Columns Definition
  const columns = useMemo<ColumnDef<FormResponse>[]>(
    () => [
      {
        id: "respondent",
        header: "Respondent",
        cell: ({ row }) => {
          const resp = row.original;
          const fan = resp.fan;

          if (!fan) {
            return <span className="text-slate-500 font-semibold text-xs font-sans">Anonymous</span>;
          }

          return (
            <div className="flex flex-col gap-1.5 py-1">
              <span className="font-semibold text-slate-800 text-sm leading-none">{fan.displayName}</span>
              <div className="flex flex-col gap-1">
                {fan.email && <EmailLink email={fan.email} />}
                {fan.phoneNumber && <PhoneLink phoneNumber={fan.phoneNumber} />}
              </div>
            </div>
          );
        },
      },
      {
        id: "player",
        header: "Assigned Player",
        cell: ({ row }) => {
          const resp = row.original;
          const player = resp.player;

          if (!player) {
            return <span className="text-slate-400 italic text-xs font-sans">Unassigned</span>;
          }

          return (
            <div className="flex flex-col gap-1 py-1 font-sans">
              <span className="font-semibold text-slate-700 text-sm leading-none">{player.fullName || "No Name"}</span>
              {player.email && (
                <span className="text-slate-400 text-[11px] truncate max-w-[180px]" title={player.email}>
                  {player.email}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "statusAndType",
        header: "Status",
        cell: ({ row }) => {
          const resp = row.original;
          const status = resp.status || "initialized";
          const isTest = resp.isTest;

          let statusLabel = "Initialized";
          let statusVariant: "outline" | "default" | "secondary" = "outline";
          let statusClass = "";

          if (status === "in_progress") {
            statusLabel = "In Progress";
            statusVariant = "secondary";
            statusClass = "bg-sky-50 text-sky-700 border-sky-200";
          } else if (status === "completed") {
            statusLabel = "Completed";
            statusVariant = "default";
            statusClass = "bg-emerald-500 border-emerald-500 text-white";
          }

          return (
            <div className="flex flex-col gap-1.5 items-start font-sans select-none whitespace-nowrap">
              <Badge
                variant={statusVariant}
                className={cn("text-[10px] px-1.5 py-0.5 font-bold uppercase", statusClass)}
              >
                {statusLabel}
              </Badge>
              {isTest && (
                <Badge
                  variant="outline"
                  className="text-[10px] px-1.5 py-0.5 font-bold uppercase text-amber-600 border-amber-200 bg-amber-50/10"
                >
                  Test Run
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        id: "timeline",
        header: "Timing",
        cell: ({ row }) => {
          const resp = row.original;
          const startedAt = resp.createdAt;
          const completedAt = resp.completedAt;

          let startedFormatted = "-";
          if (startedAt) {
            try {
              const d = new Date(startedAt);
              startedFormatted = `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}`;
            } catch {}
          }

          let completedFormatted = "-";
          if (completedAt) {
            try {
              const d = new Date(completedAt);
              completedFormatted = `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}`;
            } catch {}
          }

          const durationStr = getDurationString(startedAt, completedAt);

          return (
            <div className="flex flex-col gap-1 text-xs font-sans text-slate-600 select-none whitespace-nowrap">
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide w-14">Started:</span>
                <span>{startedFormatted}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide w-14">Finished:</span>
                <span className={completedAt ? "text-slate-600" : "text-slate-400 italic"}>
                  {completedAt ? completedFormatted : "In Progress"}
                </span>
              </div>
              {durationStr && (
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide w-14">Duration:</span>
                  <span className="font-semibold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded text-[11px] leading-none">
                    {durationStr}
                  </span>
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: "answers",
        header: "Answers Summary",
        cell: ({ row }) => {
          const resp = row.original;
          const fieldResponses = resp.formFieldResponses || [];
          const answeredCount = fieldResponses.filter(f => f.value !== null && f.value !== undefined).length;

          if (fieldResponses.length === 0) {
            return <span className="text-slate-400 italic text-xs font-sans">No answers submitted yet</span>;
          }

          const prioritized = getPrioritizedResponse(fieldResponses);
          const showTitle =
            prioritized && !["star", "nps", "ten_point", "sentiment"].includes(prioritized.formField?.fieldType);

          return (
            <div
              className="flex items-center gap-4 select-none w-full justify-between"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex-1 min-w-0 pr-2 max-w-[450px]">
                {prioritized ? (
                  <div className="flex flex-col gap-0.5 w-full min-w-0">
                    {showTitle && (
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block truncate w-full">
                        {prioritized.formField?.title || prioritized.formField?.fieldKey || "Response"}
                      </span>
                    )}
                    <div className="truncate w-full text-xs">{renderInlineFieldValue(prioritized)}</div>
                  </div>
                ) : (
                  <span className="text-slate-400 italic text-xs font-sans">No answers</span>
                )}
              </div>
              <Button
                variant="outline"
                size="xs"
                onClick={() => setViewingResponse(resp)}
                className="h-8 rounded-lg px-2.5 text-xs font-semibold flex items-center gap-1.5 shadow-sm bg-white shrink-0"
              >
                <ClipboardList className="size-3.5 text-primary" />
                <span>View {answeredCount} Answers</span>
              </Button>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange} size="full">
        <Dialog.Header>
          <Dialog.Title>Form Responses</Dialog.Title>
          <Dialog.Description>
            View and filter all submitted responses for <strong>{form?.displayName || "this form"}</strong>.
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Content className="flex flex-col gap-5 py-4 overflow-y-auto max-h-[75vh] min-h-[75vh]">
          {/* Filters & Export Action */}
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between select-none w-full border-b border-slate-100 pb-3">
            <div className="flex flex-col sm:flex-row gap-4 items-center w-full lg:w-auto flex-1">
              {!isPlayer && (
                <div className="flex items-center gap-2 w-full sm:w-64">
                  <Combobox {...players} placeholder="Filter by Player..." className="flex-1 bg-white" showClear />
                </div>
              )}

              <div className="flex items-center gap-2 w-full sm:w-64">
                <Combobox
                  {...fans}
                  placeholder={
                    isPlayer ? "Filter by Fan..." : players.value ? "Filter by Player's Fans..." : "Filter by Fan..."
                  }
                  className="flex-1 bg-white"
                  showClear
                />
              </div>

              <div className="flex items-center gap-2 w-full sm:w-64">
                <Combobox
                  multiple
                  items={STATUS_OPTIONS}
                  value={selectedStatuses}
                  onValueChange={setSelectedStatuses}
                  placeholder="Filter by Status..."
                  className="flex-1 bg-white"
                  showClear
                />
              </div>
            </div>

            {/* Export button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportToXlsx}
              disabled={isExporting || isLoading || !data?.content?.length}
              className="w-full lg:w-auto shrink-0 font-sans font-bold flex items-center justify-center gap-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200 hover:border-emerald-300 transition-colors h-9 px-4 rounded-lg shadow-2xs hover:shadow-xs"
            >
              {isExporting ? (
                <>
                  <span className="size-4 rounded-full border-2 border-emerald-700 border-t-transparent animate-spin" />
                  <span>Exporting ({exportProgress}%)</span>
                </>
              ) : (
                <>
                  <svg className="size-4 shrink-0 fill-current" viewBox="0 0 24 24">
                    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                  </svg>
                  <span>Export to Excel</span>
                </>
              )}
            </Button>
          </div>

          {/* Responses Data Table */}
          <div className="flex flex-col flex-1 min-h-[300px] h-full">
            <DataTable
              columns={columns}
              data={data?.content ?? []}
              loading={isLoading}
              pagination={pagination}
              onPaginationChange={setPagination}
              pageCount={data?.pagination?.totalPages}
              rowCount={data?.pagination?.totalCount}
              scrollable
            />
          </div>
        </Dialog.Content>

        <Dialog.Footer>
          <Button onClick={() => onOpenChange(false)} type="button">
            Close
          </Button>
        </Dialog.Footer>
      </Dialog>

      {/* Detailed Response view nested modal */}
      {viewingResponse && (
        <Dialog
          open={!!viewingResponse}
          onOpenChange={open => {
            if (!open) setViewingResponse(null);
          }}
          size="md"
        >
          <Dialog.Header>
            <Dialog.Title className="flex items-center gap-2 text-base font-bold font-sans select-none">
              <ClipboardList className="size-5 text-primary" />
              <span>Response Details</span>
            </Dialog.Title>
            <Dialog.Description className="select-none">
              Detailed questions and answers submitted by{" "}
              <strong>{viewingResponse.fan?.displayName || viewingResponse.player?.fullName || "Anonymous"}</strong>.
            </Dialog.Description>
          </Dialog.Header>

          <Dialog.Content className="max-h-[55vh] overflow-y-auto space-y-4 py-4 px-6">
            {(viewingResponse.formFieldResponses || []).map((fieldResp, idx) => {
              const fieldType = fieldResp.formField?.fieldType as FormFieldType;
              const mapping = FIELD_TYPE_MAP[fieldType];
              const FieldIcon = mapping?.icon;

              return (
                <div
                  key={fieldResp.id || idx}
                  className="border border-slate-100 rounded-xl p-3.5 space-y-2 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4 select-none leading-none">
                    <div className="text-[10px] font-bold text-slate-400 tracking-wider">
                      {fieldResp.formField?.title || fieldResp.formField?.fieldKey || `Question ${idx + 1}`}
                    </div>
                    {FieldIcon && <FieldIcon className="size-4 text-slate-400 shrink-0" title={mapping.label} />}
                  </div>
                  <div className="font-sans leading-normal">{renderFieldValue(fieldResp)}</div>
                </div>
              );
            })}
          </Dialog.Content>

          <Dialog.Footer className="select-none">
            <Button onClick={() => setViewingResponse(null)} type="button">
              Close Details
            </Button>
          </Dialog.Footer>
        </Dialog>
      )}
    </>
  );
}
